import React, { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { FiPlus, FiRepeat, FiCheck, FiX } from 'react-icons/fi';
import AppLayout from '../../components/layout/AppLayout';
import PageHeader from '../../components/ui/PageHeader';
import Button from '../../components/ui/Button';
import DataTable from '../../components/ui/DataTable';
import SearchInput from '../../components/ui/SearchInput';
import StatusPill from '../../components/ui/StatusPill';
import Badge from '../../components/ui/Badge';
import ErrorBanner from '../../components/ui/ErrorBanner';
import EmptyState from '../../components/ui/EmptyState';
import { Select } from '../../components/ui/Input';
import CurrencyDisplay from '../../components/shared/CurrencyDisplay';
import DateDisplay from '../../components/shared/DateDisplay';
import { listSubscriptions } from '../../api/subscriptions';
import { BillingCycle, SubscriptionStatus } from '../../enums';
import SubscriptionForm from './SubscriptionForm';
import SubscriptionDetailDrawer from './SubscriptionDetailDrawer';

const CYCLE_FILTERS = [
  { value: 'ALL', label: 'All cycles' },
  { value: BillingCycle.MONTHLY, label: 'Monthly' },
  { value: BillingCycle.QUARTERLY, label: 'Quarterly' },
  { value: BillingCycle.ANNUALLY, label: 'Annually' },
];

const STATUS_FILTERS = [
  { value: 'ALL', label: 'All statuses' },
  { value: SubscriptionStatus.ACTIVE, label: 'Active' },
  { value: SubscriptionStatus.PAUSED, label: 'Paused' },
  { value: SubscriptionStatus.CANCELLED, label: 'Cancelled' },
  { value: SubscriptionStatus.EXPIRED, label: 'Expired' },
];

export default function SubscriptionsListPage() {
  const [cycle, setCycle] = useState('ALL');
  const [status, setStatus] = useState('ALL');
  const [search, setSearch] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [selected, setSelected] = useState(null);

  const query = useQuery({
    queryKey: ['subscriptions'],
    queryFn: listSubscriptions,
  });

  const raw = query.data || [];

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return raw.filter((s) => {
      if (cycle !== 'ALL' && s.billingCycle !== cycle) return false;
      if (status !== 'ALL' && s.status !== status) return false;
      if (q) {
        const plan = String(s.planName || '').toLowerCase();
        if (!plan.includes(q)) return false;
      }
      return true;
    });
  }, [raw, cycle, status, search]);

  const stats = useMemo(() => {
    const total = raw.length;
    let active = 0;
    let mrr = 0;
    for (const s of raw) {
      if (s.status === SubscriptionStatus.ACTIVE) {
        active += 1;
        const amt = Number(s.amount) || 0;
        if (s.billingCycle === BillingCycle.MONTHLY) mrr += amt;
        else if (s.billingCycle === BillingCycle.QUARTERLY) mrr += amt / 3;
        else if (s.billingCycle === BillingCycle.ANNUALLY) mrr += amt / 12;
      }
    }
    return { total, active, mrr };
  }, [raw]);

  const columns = [
    {
      key: 'planName',
      header: 'Plan',
      render: (r) => (
        <div className="min-w-0">
          <div className="truncate font-medium text-primary">
            {r.planName || `#${r.id}`}
          </div>
          <div className="text-[10px] uppercase tracking-wide text-ink-faint">
            ID #{r.id}
          </div>
        </div>
      ),
    },
    {
      key: 'customerId',
      header: 'Customer',
      render: (r) => (r.customerId != null ? `#${r.customerId}` : '—'),
    },
    {
      key: 'amount',
      header: 'Amount',
      align: 'right',
      render: (r) => <CurrencyDisplay value={r.amount} />,
    },
    {
      key: 'billingCycle',
      header: 'Billing Cycle',
      render: (r) =>
        r.billingCycle ? (
          <Badge tone="info">
            {String(r.billingCycle).replace(/_/g, ' ')}
          </Badge>
        ) : (
          <span className="text-ink-faint">—</span>
        ),
    },
    {
      key: 'startDate',
      header: 'Start',
      render: (r) => <DateDisplay value={r.startDate} />,
    },
    {
      key: 'endDate',
      header: 'End',
      render: (r) => <DateDisplay value={r.endDate} />,
    },
    {
      key: 'autoRenew',
      header: 'Auto-Renew',
      align: 'center',
      render: (r) =>
        r.autoRenew ? (
          <span className="inline-flex items-center gap-1 text-success">
            <FiCheck /> <span className="text-xs font-medium">Yes</span>
          </span>
        ) : (
          <span className="inline-flex items-center gap-1 text-ink-muted">
            <FiX /> <span className="text-xs">No</span>
          </span>
        ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (r) => <StatusPill value={r.status} />,
    },
    {
      key: 'actions',
      header: 'Actions',
      align: 'right',
      render: (r) => (
        <Button
          variant="ghost"
          size="sm"
          onClick={(e) => {
            e.stopPropagation();
            setSelected(r);
          }}
        >
          View
        </Button>
      ),
    },
  ];

  return (
    <AppLayout title="Subscriptions" breadcrumb="Finance / Subscriptions">
      <PageHeader
        title="Subscriptions"
        description="Manage recurring revenue plans for your customers."
        actions={
          <Button icon={FiPlus} onClick={() => setCreateOpen(true)}>
            New Subscription
          </Button>
        }
      />

      <div className="mb-4 grid grid-cols-1 gap-3 sm:grid-cols-3">
        <StatCard label="Total subscriptions" value={stats.total} />
        <StatCard label="Active" value={stats.active} tone="success" />
        <StatCard
          label="Estimated MRR"
          value={<CurrencyDisplay value={stats.mrr} />}
          tone="primary"
        />
      </div>

      <div className="mb-4 flex flex-wrap items-center gap-3 rounded-lg border border-slate-200 bg-white p-3 shadow-card">
        <div className="flex flex-wrap items-center gap-2">
          <label className="text-xs font-medium uppercase tracking-wide text-ink-muted">
            Cycle
          </label>
          <Select
            value={cycle}
            onChange={(e) => setCycle(e.target.value)}
            className="w-40"
          >
            {CYCLE_FILTERS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </Select>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <label className="text-xs font-medium uppercase tracking-wide text-ink-muted">
            Status
          </label>
          <Select
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            className="w-40"
          >
            {STATUS_FILTERS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </Select>
        </div>
        <div className="flex-1" />
        <SearchInput
          value={search}
          onChange={setSearch}
          placeholder="Search by plan name..."
          className="w-full sm:w-80"
        />
      </div>

      {query.error && (
        <div className="mb-4">
          <ErrorBanner
            error={query.error}
            title="Could not load subscriptions"
            onRetry={query.refetch}
          />
        </div>
      )}

      <DataTable
        columns={columns}
        rows={filtered}
        loading={query.isLoading}
        error={!!query.error}
        empty={
          <EmptyState
            icon={FiRepeat}
            title={
              search || cycle !== 'ALL' || status !== 'ALL'
                ? 'No subscriptions match your filters'
                : 'No subscriptions yet'
            }
            description={
              search || cycle !== 'ALL' || status !== 'ALL'
                ? 'Try clearing filters or adjusting your search.'
                : 'Create your first subscription to start tracking recurring revenue.'
            }
            action={
              !search && cycle === 'ALL' && status === 'ALL' ? (
                <Button icon={FiPlus} onClick={() => setCreateOpen(true)}>
                  New Subscription
                </Button>
              ) : null
            }
          />
        }
        onRowClick={(row) => setSelected(row)}
      />

      <SubscriptionForm
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={(data) => setSelected(data)}
      />

      <SubscriptionDetailDrawer
        open={!!selected}
        subscriptionId={selected?.id}
        fallback={selected}
        onClose={() => setSelected(null)}
      />
    </AppLayout>
  );
}

function StatCard({ label, value, tone = 'default' }) {
  const toneCls =
    tone === 'success'
      ? 'text-success'
      : tone === 'primary'
      ? 'text-primary'
      : 'text-ink';
  return (
    <div className="rounded-lg border border-slate-200 bg-white px-4 py-3 shadow-card">
      <div className="text-[10px] font-medium uppercase tracking-wide text-ink-muted">
        {label}
      </div>
      <div className={`mt-1 text-lg font-semibold ${toneCls}`}>{value}</div>
    </div>
  );
}
